import MinimizeIcon from '@material-ui/icons/Remove';

import React, { Dispatch, SetStateAction, useEffect } from 'react';

import { useTheme, withStyles, WithStyles } from '@material-ui/core/styles';
import {
    Button,
    Box,
    IconButton,
    Fade,
    Card,
    CardHeader,
    CardActions,
    useMediaQuery,
} from '@material-ui/core';

import launchPointStyles from './AlmeLaunchPoint.styles';
import AlmeInput from '../AlmeInput/AlmeInput';
import { AlmeLaunchPointConfig } from './AlmeLaunchPointConfig';
import { AlmeLaunchIvaMessageEventData } from '@alme/post-message-api/AlmeLaunchIvaMessageEventData';
import { defaultAccessKey, getAccessKeyFromQuery } from '../../utils/PersonaUtils';
import { AlmeIvaTypeProp } from '../../models/AlmeIvaTypeMapping';
import { useIntl } from 'react-intl';
import { CookieLoader } from '@alme/storage-ts/CookieLoader';

const savedAccessKey = new CookieLoader<string>('verintAccessKey');

interface AlmeLaunchPointProps extends WithStyles<typeof launchPointStyles> {
    isOpen?: boolean;
    ivaType?: AlmeIvaTypeProp;
    almeConfig: AlmeLaunchPointConfig;
    hasHistory: boolean;
}

function shouldShowLaunchPoint(ivaType?: AlmeIvaTypeProp) {
    return !ivaType || ivaType === 'embedded';
}

const sendLaunchPostMessage = (hasHistory: boolean, inputPlaceholderText: string, setLaunchQuestion: Dispatch<SetStateAction<boolean>>, inputFieldText?: string): void => {
    if (inputFieldText && !hasHistory && inputPlaceholderText) {
        setLaunchQuestion(true);
    }
    else {
        const accessKey = getAccessKeyFromQuery() || savedAccessKey.GetStorage() || defaultAccessKey;

        const data = {
            message: {
                type: "LaunchIva",
                source: "client",
                launchPointName: "Global",
                launchParameter: "MainPage",
                accessKey,
            },
        } as AlmeLaunchIvaMessageEventData;

        window.postMessage(data, window.location.origin);
    }
};

const sendInputPostMessage = (setLaunchQuestion: Dispatch<SetStateAction<boolean>>, input?: string): void => {
    const accessKey = getAccessKeyFromQuery() || savedAccessKey.GetStorage() || defaultAccessKey;

    const data = {
        message: {
            type: "LaunchIva",
            source: "client",
            launchPointName: "Global",
            launchParameter: "MainPage",
            launchQuestion: input,
            accessKey,
        },
    } as AlmeLaunchIvaMessageEventData;

    if (input) {
        setLaunchQuestion(true);
    }

    window.postMessage(data, window.location.origin);
};

const AlmeLaunchPoint: React.FC<AlmeLaunchPointProps> = (props: AlmeLaunchPointProps) => {
    /**  State variable controls if launch with question form will render, initially set to false. */
    const [launchQuestion, setLaunchQuestion] = React.useState(false);
    const intl = useIntl();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm')) && !props.almeConfig.launchPoint.inputFieldText;

    useEffect(() => {
        if (!props.isOpen) {
            setLaunchQuestion(false);
        }
    }, [props.isOpen]);

    /**
     * Upon clicking the launch icon, check if launch with question config is set, and is start of a new session
     * If conditions pass, set state variable to render launch with question.
     */
    const handleClick = (): void => {
        sendLaunchPostMessage(props.hasHistory, intl.formatMessage({
            id: "launchpoint.inputPlaceHolderText",
            defaultMessage: 'Default launchpoint input placeholderText',
            description: 'AlmeLaunchPoint input placeholder text',
        }), setLaunchQuestion, props.almeConfig.launchPoint.inputFieldText);
    };

    const handleInputClick = (input?: string): void => {
        sendInputPostMessage(setLaunchQuestion, input);
    };

    const handleMinClick = (): void => {
        if (props.almeConfig.launchPoint.inputFieldText)
            setLaunchQuestion(false);
    };

    if (!shouldShowLaunchPoint(props.ivaType)) {
        return null;
    }

    const sharedIconProps = {
        id: 'launchIvaBtn',
        onClick: handleClick,
        title: props.almeConfig.launchPoint.titleText || "Launch IVA",
    };

    return (
        <Fade in={!props.isOpen} unmountOnExit>
            <Box className={props.classes.root} id='alme-launch-point-root'>
                {
                    launchQuestion &&
                    !props.hasHistory &&
                    <Card
                        id='alme-launch-point-card'
                        variant='elevation'
                        className={props.classes.card}
                        raised
                    ><CardHeader
                        className={props.classes.cardHeader}
                        title={props.almeConfig.launchPoint.inputFieldText}
                        titleTypographyProps={{
                            align: 'left',
                            variant: 'subtitle1',
                            color: 'inherit',
                            noWrap: true,
                        }}
                        action={
                            <IconButton
                                id='alme-launch-point-min'
                                size='medium'
                                color='inherit'
                                onClick={handleMinClick}
                            ><MinimizeIcon />
                            </IconButton>
                        }>
                        </CardHeader>
                        <CardActions className={props.classes.cardActions}>
                            <AlmeInput almeConfig={props.almeConfig.launchPoint} submitFunc={handleInputClick} inputId='alme-launch-point-input-box' submitId='alme-launch-point-submit-button' ></AlmeInput>
                        </CardActions>
                    </Card>
                }
                {
                    !launchQuestion &&
                    props.almeConfig.launchPoint.launchIcon &&
                    <IconButton
                        {...sharedIconProps}
                        className={props.classes.icon}
                    >{props.almeConfig.launchPoint.launchIcon}</IconButton>
                }
                {
                    !launchQuestion &&
                    props.almeConfig.launchPoint.buttonText &&
                    <Button
                        {...sharedIconProps}
                        variant={props.almeConfig.launchPoint.launchButton.variant}
                        fullWidth={isSmall}
                        className={props.classes.button}
                    >{props.almeConfig.launchPoint.buttonText}</Button>
                }
            </Box>
        </Fade>
    );
};

export default withStyles(launchPointStyles)(AlmeLaunchPoint);
